<template>
  <div class="flex h-full">

    <!-- New PageSidebar Component -->
    <PageSidebar
      title="Admin Console"
      :items="sidebarItems"
      @item-click="handleSidebarItemClick"
      @child-click="handleSidebarChildClick"
    />

    <!-- Main Admin Content -->
    <div class="flex-1 h-full overflow-x-auto bg-gray-50">
      <router-view v-if="isUserDetail" />
      <template v-else>
        <div v-if="selected_tab === 'IAM Console'">
          <IAM_Console />
        </div>
        <div v-if="selected_tab === 'Opportunity/Task Admin'">
          <TaskAdmin />
        </div>
        <div v-if="selected_tab === 'Workflow Configuration'">
          <WorkflowConfigManager />
        </div>
        <div v-if="selected_tab === 'InfoTag Manager'">
          <Overview_InfoTagManager />
        </div>
        <div v-if="selected_tab === 'Entity Management'">
          <EntityManagementContainer />
        </div>
        <div v-if="selected_tab === 'Performance Domains & Metrics'">
          <MetricStudio />
        </div>
        <div v-if="selected_tab === 'Alert Management'">
          <AlertInstanceAdmin />
        </div>
        <div v-if="selected_tab === 'Care Management'">
          <CareManagement_Admin />
        </div>
        <div v-if="selected_tab === 'Page Configuration'">
          <PageConfigurationAdmin />
        </div>
        <div v-if="selected_tab === 'Telephony'">
          <Telephony_Admin />
        </div>
        <div v-if="selected_tab === 'Agent Pages'">
          <AgentPages_Admin />
        </div>
        <div v-if="selected_tab === 'Hermes'">
          <Hermes_Admin />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import IAM_Console from '@/components/Overview/Overview_IAM_Console.vue';
import TaskAdmin from '@/components/Portal/Admin/TaskAdmin.vue';
import WorkflowConfigManager from '@/components/Portal/Admin/WorkflowConfigManager.vue';
import Overview_InfoTagManager from '@/components/Overview/Overview_InfoTagManager.vue';
import MetricStudio from "@/components/Admin/MetricStudio/MetricStudio.vue";
import AlertInstanceAdmin from "@/components/Metric/Metric_Builder/AlertInstanceAdmin.vue";
import CareManagement_Admin from '@/components/Care_Management/CareManagement_Admin.vue';
import PageConfigurationAdmin from '@/components/Overview/PageConfigurationAdmin.vue';
import Telephony_Admin from '@/components/Admin/Telephony_Admin.vue';
import AgentPages_Admin from '@/components/Admin/AgentPages_Admin.vue';
import Hermes_Admin from '@/components/Admin/Hermes/Hermes_Admin.vue';
import PageSidebar from '@/components/widgetcomponents/library/PageSidebar.vue';
import { mapGetters } from 'vuex';
import EntityManagementContainer from "@/components/Entity/EntityManagementContainer.vue";
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import { metricStudioLocation } from '../../../router/tenantadmin/metric_studio_routes.js';

const TAB_ROUTE_MAP = {
  'IAM Console': 'AdminIAMUsers',
  'Opportunity/Task Admin': 'AdminTaskAdmin',
  'InfoTag Manager': 'AdminInfoTagManager',
  'Entity Management': 'AdminEntityConfigurationTypes',
  'Care Management': 'AdminCareManagement',
  'Workflow Configuration': 'AdminWorkflowTaskConfiguration',
  'Performance Domains & Metrics': metricStudioLocation({ artifact: 'metric' }),
  'Alert Management': 'AdminAlertManagement',
  'Page Configuration': 'AdminPageConfiguration',
  'Telephony': 'AdminTelephonyPhoneBook',
  'Agent Pages': 'AdminAgentPages',
  'Hermes': 'AdminHermesOverview',
};

export default {
  components: {
    EntityManagementContainer,
    MetricStudio,
    AlertInstanceAdmin,
    TaskAdmin,
    IAM_Console,
    WorkflowConfigManager,
    Overview_InfoTagManager,
    CareManagement_Admin,
    PageConfigurationAdmin,
    Telephony_Admin,
    AgentPages_Admin,
    Hermes_Admin,
    PageSidebar,
  },
  data() {
    return {
      sidebarLoading: false,
    };
  },
  computed: {
    isUserDetail() {
      return this.$route.name === 'AdminIAMUserDetail';
    },
    selected_tab() {
      return this.$route.meta.adminTab || 'IAM Console';
    },
    isSuperUser() {
      return this.current_user?.is_superuser || false
    },
    ...mapGetters('auth', ['isAuthenticated', 'current_user']),
    ...mapGetters('tenant', ['isTenantAdmin']),
    performance_summary_tabs() {
      const tabs = ['IAM Console'];
      if (this.isTenantAdmin) {
        if (canAccessFeature(FEATURES.MODULE_ADMIN_TASK)) tabs.push('Opportunity/Task Admin');
        tabs.push('InfoTag Manager', 'Entity Management');
        if (canAccessFeature(FEATURES.MODULE_ADMIN_CARE_MANAGEMENT)) tabs.push('Care Management');
        if (canAccessFeature(FEATURES.MODULE_ADMIN_WORKFLOW)) tabs.push('Workflow Configuration');
        tabs.push('Performance Domains & Metrics', 'Alert Management', 'Page Configuration');
      }
      return tabs;
    },
    sidebarItems() {
      return [
        {
          id: 'IAM Console',
          label: 'IAM Console',
          active: this.selected_tab === 'IAM Console',
        },
        {
          id: 'Opportunity/Task Admin',
          label: 'Opportunity/Task Admin',
          active: this.selected_tab === 'Opportunity/Task Admin',
          canAccess: FEATURES.MODULE_ADMIN_TASK,
        },
        {
          id: 'InfoTag Manager',
          label: 'InfoTag Manager',
          active: this.selected_tab === 'InfoTag Manager',
        },
        {
          id: 'Entity Management',
          label: 'Entity Management',
          active: this.selected_tab === 'Entity Management',
        },
        {
          id: 'Care Management',
          label: 'Care Management',
          active: this.selected_tab === 'Care Management',
          canAccess: FEATURES.MODULE_ADMIN_CARE_MANAGEMENT,
        },
        {
          id: 'Workflow Configuration',
          label: 'Workflow Configuration',
          active: this.selected_tab === 'Workflow Configuration',
          canAccess: FEATURES.MODULE_ADMIN_WORKFLOW,
        },
        {
          id: 'Performance Domains & Metrics',
          label: 'Performance Domains & Metrics',
          active: this.selected_tab === 'Performance Domains & Metrics',
        },
        {
          id: 'Alert Management',
          label: 'Alert Management',
          active: this.selected_tab === 'Alert Management',
        },
        {
          id: 'Page Configuration',
          label: 'Page Configuration',
          active: this.selected_tab === 'Page Configuration',
        },
        {
          id: 'Telephony',
          label: 'Telephony',
          active: this.selected_tab === 'Telephony',
          // Every Telephony tab needs the telehealth phone feature; without it the section would be empty.
          canAccess: FEATURES.COMPONENT_TELEHEALTH_PHONE,
        },
        {
          id: 'Agent Pages',
          label: 'Agent Pages',
          active: this.selected_tab === 'Agent Pages',
          canAccess: FEATURES.MODULE_AGENT,
        },
        {
          id: 'Hermes',
          label: 'Hermes',
          active: this.selected_tab === 'Hermes',
          canAccess: FEATURES.MODULE_AGENT,
        },
      ]
    },
  },
  methods: {
    switch_tab(tab) {
      const target = TAB_ROUTE_MAP[tab];
      if (!target) return;
      const location = typeof target === 'string' ? { name: target } : target;
      if (this.$route.fullPath !== this.$router.resolve(location).fullPath) {
        this.$router.push(location);
      }
    },
    handleSidebarItemClick(item) {
      this.switch_tab(item.id);
    },
  },
};
</script>
